import { toast } from 'sonner'
import { NodeForm } from '@/components/nodes/NodeForm'
import { User, UserRole, Timezone, UserStatus, Workspace, Project } from '@/types'
import { type UserCreateFormData, type UserEditFormData } from '@/lib/validations/user'
import { nodeFormConfigs } from '@/lib/config/node-form-configs'

export interface UserFormProps {
  mode: 'create' | 'edit'
  user?: User | null // For edit mode
  workspaces?: Workspace[] // For create mode
  allProjects?: Project[] // For create mode
  selectedWorkspace?: Workspace | null // For create mode (single selection)
  selectedProjects?: Project[] // For create mode
  onWorkspaceChange?: (workspace: Workspace | null) => void // For create mode (single selection)
  onProjectsChange?: (projects: Project[]) => void // For create mode
  onSubmit: (data: UserCreateFormData | UserEditFormData) => Promise<void>
  onCancel: () => void
  cancelText?: string
  loading?: boolean
  onDirtyChange?: (isDirty: boolean) => void
}

export function UserForm({
  mode,
  user,
  workspaces = [],
  allProjects = [],
  selectedWorkspace = null,
  selectedProjects = [],
  onWorkspaceChange,
  onProjectsChange,
  onSubmit,
  onCancel,
  cancelText,
  loading = false,
  onDirtyChange,
}: UserFormProps) {
  const handleSubmit = async (data: Record<string, unknown>) => {
    try {
      // Transform the data to match the expected schema
      const transformedData = {
        name: data.name as string,
        email: data.email as string,
        location: data.location as string,
        role: data.role as UserRole,
        timezone: data.timezone as Timezone,
        status: data.userStatus as UserStatus,
        ...(mode === 'create' && {
          password: data.password as string,
          confirmPassword: data.confirmPassword as string,
          workspaceIds: selectedWorkspace ? [selectedWorkspace.id] : [],
          projectIds: selectedProjects.map((p) => p.id),
        }),
      }
      await onSubmit(transformedData as UserCreateFormData | UserEditFormData)
    } catch (error) {
      console.error(`Failed to ${mode} user:`, error)
      toast.error(`Failed to ${mode} user`)
    }
  }

  const config = mode === 'create' ? nodeFormConfigs.user : nodeFormConfigs.userEdit

  const initialData =
    mode === 'edit' && user
      ? {
          name: user.name,
          email: user.email,
          location: user.location || '',
          role: user.role,
          timezone: user.timezone,
          userStatus: user.status,
        }
      : {
          role: UserRole.SuperAdmin,
          timezone: Timezone.UTC,
          userStatus: UserStatus.Online,
        }

  return (
    <NodeForm
      mode={mode}
      config={config}
      initialData={initialData}
      initialProjects={selectedProjects}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitText={
        loading
          ? mode === 'create'
            ? 'Creating...'
            : 'Saving...'
          : mode === 'create'
            ? 'Create User'
            : 'Save Changes'
      }
      cancelText={cancelText}
      loading={loading}
      title={mode === 'create' ? 'User Details' : 'User Information'}
      description={
        mode === 'create'
          ? 'Fill in the details below to create a new user'
          : 'Update user information'
      }
      // External data management for create mode
      externalWorkspaces={workspaces}
      externalProjects={allProjects}
      externalSelectedProjects={selectedProjects}
      onExternalWorkspaceChange={(workspaceId) => {
        const workspace = workspaces.find((w) => w.id === workspaceId)
        onWorkspaceChange?.(workspace || null)
      }}
      onExternalProjectAdd={(projectId) => {
        const project = allProjects.find((p) => p.id === projectId)
        if (project && !selectedProjects.some((p) => p.id === projectId)) {
          onProjectsChange?.([...selectedProjects, project])
        }
      }}
      onExternalProjectRemove={(projectId) => {
        onProjectsChange?.(selectedProjects.filter((p) => p.id !== projectId))
      }}
      onDirtyChange={onDirtyChange}
    />
  )
}
